/**
 * Shot preset — plain serializable object, one source of truth for preview,
 * record, and batch. Flight code receives this; it never reads UI state.
 */

import { z } from "zod";

export const ShotAspectSchema = z.enum(["9:16", "1:1", "16:9"]);
export type ShotAspect = z.infer<typeof ShotAspectSchema>;

export const SHOT_ASPECT_PIXELS: Record<ShotAspect, { width: number; height: number }> = {
  "9:16": { width: 1080, height: 1920 },
  "1:1": { width: 1080, height: 1080 },
  "16:9": { width: 1920, height: 1080 },
};

const EndAltitudeByAspectSchema = z.object({
  "9:16": z.number().min(0.1).max(1.5),
  "1:1": z.number().min(0.1).max(1.5),
  "16:9": z.number().min(0.1).max(1.5),
});

export const ShotPresetValuesSchema = z.object({
  startAltitude: z.number().min(1).max(4),
  endAltitudeByAspect: EndAltitudeByAspectSchema,
  offsetLng: z.number().min(-90).max(90),
  offsetLat: z.number().min(-60).max(60),
  durationMs: z.number().int().min(1000).max(10_000),
  holdBeforeMs: z.number().int().min(0).max(3000),
  holdAfterMs: z.number().int().min(0).max(5000),
  arrivalRipple: z.boolean(),
  /** Accent outline on the target country for the shot window. */
  highlightBorder: z.boolean().default(true),
});
export type ShotPresetValues = z.infer<typeof ShotPresetValuesSchema>;

export const NamedShotPresetSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1).max(40),
  values: ShotPresetValuesSchema,
});
export type NamedShotPreset = z.infer<typeof NamedShotPresetSchema>;

/** Absolute floor for shot end altitude (below browse MIN_CAMERA_ALTITUDE). */
export const SHOT_MIN_ALTITUDE = 0.1;

export const DEFAULT_SHOT_VALUES: ShotPresetValues = {
  startAltitude: 2.5,
  endAltitudeByAspect: {
    "9:16": 0.45,
    "1:1": 0.45,
    "16:9": 0.45,
  },
  offsetLng: -35,
  offsetLat: 15,
  durationMs: 4000,
  holdBeforeMs: 600,
  holdAfterMs: 1800,
  // Off by default — rings were the end-of-shot stutter in capture.
  arrivalRipple: false,
  highlightBorder: true,
};

/** Must match SHOT_RIPPLE timing in flyTo.ts (450ms × 1). */
export const ARRIVAL_RIPPLE_MS = 450;

/** Hold-before + flight + hold-after (excludes fixed ripple). */
export function shotScalableDurationMs(values: ShotPresetValues): number {
  return values.holdBeforeMs + values.durationMs + values.holdAfterMs;
}

/** Full timeline: holds + flight + optional ripple. */
export function shotTotalDurationMs(values: ShotPresetValues): number {
  return (
    shotScalableDurationMs(values) +
    (values.arrivalRipple ? ARRIVAL_RIPPLE_MS : 0)
  );
}

function clampInt(n: number, min: number, max: number): number {
  return Math.round(Math.min(max, Math.max(min, n)));
}

/**
 * Scale hold-before / flight / hold-after linearly so the full shot length
 * (including ripple if on) matches `targetTotalMs`. Ripple stays fixed.
 */
export function scaleShotTimingToTotal(
  values: ShotPresetValues,
  targetTotalMs: number,
): Pick<ShotPresetValues, "durationMs" | "holdBeforeMs" | "holdAfterMs"> {
  const ripple = values.arrivalRipple ? ARRIVAL_RIPPLE_MS : 0;
  const current = shotScalableDurationMs(values);
  const targetScalable = Math.max(1000, targetTotalMs - ripple);
  if (current <= 0) {
    return { durationMs: targetScalable, holdBeforeMs: 0, holdAfterMs: 0 };
  }
  const s = targetScalable / current;
  let holdBeforeMs = clampInt(values.holdBeforeMs * s, 0, 3000);
  let durationMs = clampInt(values.durationMs * s, 1000, 10_000);
  let holdAfterMs = clampInt(values.holdAfterMs * s, 0, 5000);
  // Put clamp drift back onto flight duration when possible.
  const drift = targetScalable - (holdBeforeMs + durationMs + holdAfterMs);
  if (drift !== 0) {
    durationMs = clampInt(durationMs + drift, 1000, 10_000);
  }
  return { holdBeforeMs, durationMs, holdAfterMs };
}

/** Master slider range for total shot length (ms). */
export const SHOT_TOTAL_MIN_MS = 2000;
export const SHOT_TOTAL_MAX_MS = 18_000;

export const SLOW_SHOT_VALUES: ShotPresetValues = {
  ...DEFAULT_SHOT_VALUES,
  durationMs: 6500,
  holdBeforeMs: 800,
  holdAfterMs: 2200,
  startAltitude: 2.8,
};

export const SEED_SHOT_PRESETS: NamedShotPreset[] = [
  { id: "preset_standard", name: "Standard", values: DEFAULT_SHOT_VALUES },
  { id: "preset_slow", name: "Slow", values: SLOW_SHOT_VALUES },
];

export function endAltitudeForAspect(
  values: ShotPresetValues,
  aspect: ShotAspect,
): number {
  return Math.max(SHOT_MIN_ALTITUDE, values.endAltitudeByAspect[aspect]);
}

export function newPresetId(): string {
  return `preset_${crypto.randomUUID().slice(0, 8)}`;
}
